import { useState } from 'react';
import { CaretLeft, CaretRight, FileArrowDown, SpinnerGap } from '@phosphor-icons/react';

export interface RechnungsExportWahl {
  format: 'csv' | 'xlsx' | 'pdf';
  umfang: 'ansicht' | 'alle';
  richtung: 'alle' | 'eingang' | 'ausgang';
  status: 'alle' | 'offen' | 'bezahlt' | 'overdue';
  zeitraum: 'alle' | 'monat' | 'jahr';
}
type Feld = keyof RechnungsExportWahl;

const GOLD = '#e2c08d';

/**
 * Export-Assistent der Rechnungen (Format → Umfang → Richtung → Status → Zeitraum → Zusammenfassung) –
 * Schritte und Texte wie bisher in der Pillen-Steuerung, jetzt als Pillen-Formular mit eigenem Zustand.
 */
export function RechnungsExportAssistent({ anzahlAnsicht, onExport, onFertig }: {
  anzahlAnsicht: number;
  /** führt den Export aus, liefert die Anzahl exportierter Belege */
  onExport: (wahl: RechnungsExportWahl) => Promise<number>;
  onFertig: (anzahl: number) => void;
}) {
  const SCHRITTE: { title: string; field: Feld; options: { v: string; l: string }[] }[] = [
    { title: 'Format', field: 'format', options: [{ v: 'csv', l: 'CSV' }, { v: 'xlsx', l: 'Excel' }, { v: 'pdf', l: 'PDF (Bericht)' }] },
    { title: 'Umfang', field: 'umfang', options: [{ v: 'ansicht', l: `Aktuelle Ansicht (${anzahlAnsicht})` }, { v: 'alle', l: 'Alle Belege' }] },
    { title: 'Richtung', field: 'richtung', options: [{ v: 'alle', l: 'Alle' }, { v: 'eingang', l: 'Eingehend' }, { v: 'ausgang', l: 'Ausgehend' }] },
    { title: 'Status', field: 'status', options: [{ v: 'alle', l: 'Alle' }, { v: 'offen', l: 'Offen' }, { v: 'bezahlt', l: 'Bezahlt' }, { v: 'overdue', l: 'Überfällig' }] },
    { title: 'Zeitraum', field: 'zeitraum', options: [{ v: 'alle', l: 'Alle' }, { v: 'monat', l: 'Dieser Monat' }, { v: 'jahr', l: 'Dieses Jahr' }] },
  ];
  const ZUSAMMENFASSUNG: Record<Feld, Record<string, string>> = {
    format: { csv: 'CSV', xlsx: 'Excel', pdf: 'PDF (Bericht)' },
    umfang: { ansicht: 'Aktuelle Ansicht', alle: 'Alle Belege' },
    richtung: { alle: 'Alle', eingang: 'Eingehend', ausgang: 'Ausgehend' },
    status: { alle: 'Alle', offen: 'Offen', bezahlt: 'Bezahlt', overdue: 'Überfällig' },
    zeitraum: { alle: 'Alle', monat: 'Dieser Monat', jahr: 'Dieses Jahr' },
  };

  const [schritt, setSchritt] = useState(0);
  const [wahl, setWahl] = useState<Partial<RechnungsExportWahl>>({ umfang: 'ansicht', richtung: 'alle', status: 'alle', zeitraum: 'alle' });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(false);
  const zusammenfassung = schritt >= SCHRITTE.length;

  const exportieren = async () => {
    if (!wahl.format || laeuft) return;
    setLaeuft(true);
    setFehler(false);
    try {
      const anzahl = await onExport(wahl as RechnungsExportWahl);
      onFertig(anzahl);
    } catch {
      setFehler(true);
      setLaeuft(false);
    }
  };

  const knopf: React.CSSProperties = { border: 'none', fontFamily: 'inherit', cursor: 'pointer', color: '#fff' };

  return (
    <div className="flex flex-col text-white" style={{ gap: 14 }}>
      <div className="flex items-center justify-between">
        <span className="uppercase" style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', color: 'rgba(235,235,245,0.6)' }}>
          {zusammenfassung ? 'Zusammenfassung' : `${SCHRITTE[schritt]!.title} · ${schritt + 1}/${SCHRITTE.length}`}
        </span>
        {schritt > 0 && (
          <button type="button" onClick={() => setSchritt(s => s - 1)} className="flex items-center"
            style={{ ...knopf, height: 36, gap: 4, padding: '0 10px', borderRadius: 18, background: 'rgba(255,255,255,0.1)', fontSize: 13, fontWeight: 700 }}>
            <CaretLeft size={13} weight="bold" /> Zurück
          </button>
        )}
      </div>
      <div className="flex" style={{ gap: 4 }} aria-hidden>
        {SCHRITTE.map((_, i) => (
          <span key={i} className="flex-1" style={{ height: 4, borderRadius: 2, background: i < schritt ? GOLD : i === schritt && !zusammenfassung ? 'rgba(226,192,141,0.5)' : 'rgba(255,255,255,0.12)', transition: 'background .3s' }} />
        ))}
      </div>
      {!zusammenfassung ? (
        <div className="flex flex-col" style={{ gap: 6 }}>
          {SCHRITTE[schritt]!.options.map(opt => {
            const feld = SCHRITTE[schritt]!.field;
            const gewaehlt = wahl[feld] === opt.v;
            return (
              <button key={opt.v} type="button"
                onClick={() => { setWahl(w => ({ ...w, [feld]: opt.v })); setSchritt(s => s + 1); }}
                className="flex items-center justify-between transition-colors hover:bg-white/[0.12]"
                style={{ ...knopf, height: 48, padding: '0 14px', borderRadius: 14, fontSize: 14, fontWeight: 700, background: gewaehlt ? 'rgba(226,192,141,0.22)' : 'rgba(255,255,255,0.07)' }}>
                {opt.l}
                <CaretRight size={14} weight="bold" style={{ opacity: 0.45 }} />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col" style={{ gap: 10 }}>
          <div className="flex flex-col" style={{ gap: 8, padding: 14, borderRadius: 16, background: 'rgba(255,255,255,0.07)' }}>
            {SCHRITTE.map(st => (
              <div key={st.field} className="flex items-center justify-between" style={{ fontSize: 13 }}>
                <span style={{ color: 'rgba(235,235,245,0.6)' }}>{st.title}</span>
                <span style={{ fontWeight: 700 }}>{ZUSAMMENFASSUNG[st.field][wahl[st.field] ?? ''] ?? '–'}</span>
              </div>
            ))}
          </div>
          {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: '#ff6961' }}>Export fehlgeschlagen</span>}
          <button type="button" disabled={laeuft || !wahl.format} onClick={() => { void exportieren(); }} className="flex items-center justify-center"
            style={{ ...knopf, height: 48, gap: 8, borderRadius: 14, fontSize: 15, fontWeight: 800, background: 'linear-gradient(180deg,#f0d3a3,#c9a063)', color: '#2a1d0e', opacity: laeuft ? 0.6 : 1 }}>
            {laeuft ? <SpinnerGap size={16} weight="bold" className="pille-dreh" /> : <FileArrowDown size={16} weight="bold" />} Bericht erstellen
          </button>
        </div>
      )}
    </div>
  );
}
